import React, { useMemo } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  useMediaQuery,
  Typography,
  Stack,
  Theme,
  alpha,
  Icon,
  useTheme,
} from "@mui/material";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import PersonIcon from "@mui/icons-material/Person";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import PsychologyIcon from "@mui/icons-material/Psychology";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningIcon from "@mui/icons-material/Warning";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import InsightsIcon from "@mui/icons-material/Insights";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SettingsIcon from "@mui/icons-material/Settings";
import FavoriteIcon from "@mui/icons-material/Favorite";
import BusinessIcon from "@mui/icons-material/Business";
import { ROUTES } from "../../utils";
import { useClientScope } from "../../contexts";
import { useCurrentClientDashboardSettings } from "../../hooks";
import { DirectionsRun, SelfImprovement, Spa } from "@mui/icons-material";

const DRAWER_WIDTH = 260;

interface AdminSideNavProps {
  open: boolean;
  onClose: () => void;
}

type NavigationItem = {
  text: string;
  icon: React.ReactElement;
  path: string;
  dashboardKey?: string;
};

type NavigationSection = {
  category: string;
  items: NavigationItem[];
};

const navigationItems: NavigationSection[] = [
  {
    category: "WELLNESS & HEALTH",
    items: [
      {
        text: "Programme Profile",
        icon: <SelfImprovementIcon fontSize="medium" />,
        path: ROUTES.ADMIN_DASHBOARD,
        dashboardKey: "programme_profile",
      },
      {
        text: "Wellness Overview",
        icon: <InsightsIcon fontSize="medium" />,
        path: "/admin/wellness-overview",
        dashboardKey: "wellness_overview",
      },
      {
        text: "Recovery",
        icon: <SelfImprovementIcon fontSize="medium" />,
        path: "/admin/recovery",
        dashboardKey: "recovery",
      },
      {
        text: "Sleep Analytics",
        icon: <BedtimeIcon fontSize="medium" />,
        path: "/admin/sleep-analytics",
        dashboardKey: "sleep_analytics",
      },
      {
        text: "Strain and Activity",
        icon: <DirectionsRun fontSize="medium" />,
        path: "/admin/strain-activity",
        dashboardKey: "strain_activity",
      },
      {
        text: "Overall Stress & Mind",
        icon: <Spa fontSize="medium" />,
        path: "/admin/stress-mind",
        dashboardKey: "overall_stress_mind",
      },
      {
        text: "Biometrics and Vitals",
        icon: <MonitorHeartIcon fontSize="medium" />,
        path: "/admin/biometrics",
        dashboardKey: "biometrics_vitals",
      },
      {
        text: "User Progress",
        icon: <TrendingUpIcon fontSize="medium" />,
        path: "/admin/user-progress",
        dashboardKey: "user_progress",
      },
      {
        text: "Team Progress",
        icon: <BusinessIcon fontSize="medium" />,
        path: "/admin/team-progress",
        dashboardKey: "team_progress",
      },
      {
        text: "Jeopardy Insights",
        icon: <WarningIcon fontSize="medium" />,
        path: "/admin/jeopardy-insights",
        dashboardKey: "jeopardy_insights",
      },
      {
        text: "Measure Comparison In...",
        icon: <CompareArrowsIcon fontSize="medium" />,
        path: "/admin/measure-comparison",
        dashboardKey: "measure_comparison",
      },
      {
        text: "Predictive Insights",
        icon: <PsychologyIcon fontSize="medium" />,
        path: "/admin/predictive-insights",
        dashboardKey: "predictive_insights",
      },
    ],
  },
  /*
  {
    category: "WELLNESS PROGRAMS",
    items: [
      {
        text: "Learning Resources",
        icon: <MenuBookIcon fontSize="medium" />,
        path: "/admin/learning-resources",
      },
      {
        text: "Certificates & Badges",
        icon: <WorkspacePremiumIcon fontSize="medium" />,
        path: "/admin/certificates-badges",
      },
      {
        text: "Programs & Challenges",
        icon: <EmojiEventsIcon fontSize="medium" />,
        path: "/admin/programs-challenges",
      },
    ],
  },
  */
  {
    category: "ADMINISTRATION",
    items: [
      {
        text: "Participants",
        icon: <PersonIcon fontSize="medium" />,
        path: ROUTES.PARTICIPANTS,
      },
      {
        text: "Participant Settings",
        icon: <SettingsIcon fontSize="medium" />,
        path: ROUTES.PARTICIPANT_SETTINGS,
      },
      {
        text: "Pre-Registrations",
        icon: <PersonIcon fontSize="medium" />,
        path: ROUTES.PRE_REGISTRATIONS,
      },
      {
        text: "Wellbeing Content",
        icon: <FavoriteIcon fontSize="medium" />,
        path: ROUTES.WELLBEING_CONTENT,
      },
      {
        text: "Settings",
        icon: <SettingsIcon fontSize="medium" />,
        path: ROUTES.SETTINGS,
      },
    ],
  },
];

export const AdminSideNav: React.FC<AdminSideNavProps> = ({
  open,
  onClose,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMdUp = useMediaQuery("(min-width:900px)");
  const theme = useTheme();
  const { data: dashboardSettings } = useCurrentClientDashboardSettings();
  const { isCentralAdmin } = useClientScope();

  const enabledDashboards = useMemo(() => {
    if (!dashboardSettings?.data?.length) return null;
    const enabled = new Set(
      dashboardSettings.data
        .filter((setting) => setting.is_enabled)
        .map((setting) => setting.dashboard_key)
    );
    return enabled.size ? enabled : null;
  }, [dashboardSettings?.data]);

  const filteredNavigationItems = useMemo(() => {
    // Central admins see all dashboards
    if (isCentralAdmin) {
      return navigationItems;
    }

    // Non-central admins: filter based on client dashboard settings
    if (!enabledDashboards) return navigationItems;
    return navigationItems
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          const isParticipantsLink =
            item.path === ROUTES.PARTICIPANTS ||
            item.path === ROUTES.PARTICIPANT_SETTINGS;
          if (isParticipantsLink && !isCentralAdmin) return false;
          return item.dashboardKey ? enabledDashboards.has(item.dashboardKey) : true;
        }),
      }))
      .filter((section) => section.items.length > 0);
  }, [enabledDashboards, isCentralAdmin]);
  const renderNavItems = (items: (typeof navigationItems)[0]["items"]) => (
    <List disablePadding>
      {items.map((item) => {
        const isParticipantsRoute = item.path === ROUTES.PARTICIPANTS;
        const isActive = !!matchPath(
          { path: isParticipantsRoute ? `${item.path}/*` : item.path, end: !isParticipantsRoute },
          location.pathname
        );

        return (
          <ListItemButton
            key={item.text}
            sx={{
              pr: 1,
              pl: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: isActive
                ? (theme: Theme) => alpha(theme.palette.primary.main, 0.15)
                : "transparent",
              "&:hover": {
                backgroundColor: isActive
                  ? (theme: Theme) => alpha(theme.palette.primary.main, 0.15)
                  : (theme: Theme) => theme.palette.action.selected,
              },
            }}
            onClick={() => {
              navigate(item.path);
              if (!isMdUp) onClose();
            }}
          >
            {item.icon && (
              <ListItemIcon
                sx={{
                  minWidth: 32,
                  color: isActive ? "primary.main" : "text.secondary",
                }}
              >
                {item.icon}
              </ListItemIcon>
            )}
            <ListItemText
              primary={item.text}
              slotProps={{
                primary: {
                  sx: {
                    fontSize: 14,
                    color: isActive ? "primary.main" : "text.secondary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  },
                },
              }}
            />
          </ListItemButton>
        );
      })}
    </List>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { md: open ? DRAWER_WIDTH : 0 },
        flexShrink: { md: 0 },
        transition: "width 0.3s ease",
      }}
    >
      <Drawer
        variant={isMdUp ? "persistent" : "temporary"}
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            border: "none",
            transition: "width 0.3s ease",
            backgroundColor: (theme) =>
              !isMdUp ? theme.palette.background.default : "transparent",
          },
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <svg
              width="42"
              height="42"
              viewBox="0 0 42 42"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21.9039 13.3203C23.6048 17.567 20.8564 34 20.8564 34C20.8564 34 7.26578 23.3662 5.56368 19.1196C3.86282 14.8729 6.14064 10.1314 10.6537 8.53055C15.1656 6.92965 20.2031 9.0736 21.9039 13.3215V13.3203Z"
                fill={theme.palette.primary.light}
              />
              <path
                d="M36.3856 19.353C34.6122 23.5738 20.8401 34.0002 20.8401 34.0002C20.8401 34.0002 18.3733 17.5283 20.1467 13.3076C21.9202 9.0868 26.994 7.01942 31.4783 8.68864C35.9626 10.3579 38.159 15.1335 36.3856 19.3542V19.353Z"
                fill={theme.palette.secondary.main}
              />
            </svg>

            <Stack spacing={0}>
              <Typography variant="subtitle1" fontWeight={600}>
                PEAK4 Analytics
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Enterprise Health Platform
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            mt: 1,
            overflowY: "auto",
            flex: 1,
            gap: 1,
            display: "flex",
            flexDirection: "column",
            px: 2,
          }}
        >
          {filteredNavigationItems.map((section, idx) => (
            <Box key={idx}>
              {section.category && (
                <Typography variant="overline" color="text.secondary">
                  {section.category}
                </Typography>
              )}
              {renderNavItems(section.items)}
            </Box>
          ))}
        </Box>
      </Drawer>
    </Box>
  );
};
